import React from "react";

import type { Terms } from "@busbud/busbud-terms";
import type { PublicTerms } from "@busbud/core-types/dist/lib/poll-service/api";
import Alert from "@busbud/design-system-components/dist/Alert";
import Divider from "@busbud/design-system-components/dist/Divider";
import Label from "@busbud/design-system-components/dist/Label";
import Stack from "@busbud/design-system-components/dist/Stack";
import type { MappedOperator, Operator } from "@busbud/int-schemas/udm";

import { useAppContext } from "@app/helpers/hooks";
import type { NapiOperator, TicketType } from "@app/types/napi-types";

import type { TRIP_TYPES } from "../constants";
import type { Terms as NapiTerms } from "app/types/napi-types";

import { useStyles } from "./terms-full-content.styles";
import { Markdown } from "../../text-formatting/markdown";

export interface Props {
  napi_terms: NapiTerms | PublicTerms;
  ticket_type: TicketType;
  operators: Array<NapiOperator | MappedOperator | Operator>;
  trip_type: (typeof TRIP_TYPES)[keyof typeof TRIP_TYPES];
  is_post_purchase: boolean;
}

const TermsFullContent: React.FC<Props> = props => {
  const { classes } = useStyles();
  const { termsBuilder } = useAppContext();

  const { napi_terms, ticket_type, operators, trip_type, is_post_purchase } =
    props;
  const terms = termsBuilder.buildAllTerms({
    ticket_type,
    terms: napi_terms,
    operators,
    trip_type,
    options: { is_post_purchase }
  });

  function renderSection(terms: Terms, index: number, depth = 0) {
    const is_subsection = depth > 0;
    const subtitle = renderSubsectionTitle(terms, is_subsection);
    const content = renderContent(terms, depth);
    if (!content || (Array.isArray(content) && content.length === 0)) {
      return null;
    }

    return (
      // non design token values
      <Stack direction="column" spacing={is_subsection ? 1 : 2.5} key={index}>
        {subtitle}
        {content}
      </Stack>
    );
  }

  function renderSubsectionTitle(terms: Terms, is_subsection: boolean) {
    if (!terms.title || terms.title === "" || !is_subsection) {
      return null;
    }

    return (
      <Label
        size="xs"
        fontWeight="regular"
        color="text.tertiary.opaque"
        data-test-id="subsection-title"
      >
        {terms.title}
      </Label>
    );
  }

  function renderSectionTitle(terms: Terms) {
    if (!terms.title || terms.title === "") {
      return null;
    }

    return (
      <Label size="xl" color="text.primary.opaque" data-test-id="section-title">
        {terms.title}
      </Label>
    );
  }

  function renderContent(terms: Terms, depth: number) {
    if (terms.subsections) {
      return terms.subsections.map((terms_subsection, index) =>
        renderSection(terms_subsection, index, depth + 1)
      );
    } else if (terms.content) {
      return renderTermsContent(terms.content, terms.level);
    }
  }

  function renderTermsContent(content: string[] | string, level?: "info") {
    if (content === "") {
      return null;
    }

    const raw_content = Array.isArray(content) ? content : [content];
    const Wrapper = ({ children }: React.PropsWithChildren<{}>) =>
      level === "info" ? (
        <Alert
          severity="informative"
          classes={{ message: classes.alertMessage }}
        >
          {children}
        </Alert>
      ) : (
        children
      );

    return (
      <Stack direction="column" spacing={1}>
        {raw_content
          .filter(item => item !== "")
          .map((item, index) => (
            <Wrapper key={index}>
              <Markdown
                content={item}
                link_target_blank
                body_font_size={{ initial: "sm", md: "md" }}
              />
            </Wrapper>
          ))}
      </Stack>
    );
  }

  return (
    <Stack direction="column" spacing={3}>
      {terms.map((terms_section, index) => {
        const is_last_section = index === terms.length - 1;
        return (
          <React.Fragment key={index}>
            <Stack direction="column" spacing={2.5}>
              {renderSectionTitle(terms_section)}
              {renderSection(terms_section, index)}
            </Stack>
            {!is_last_section && <Divider />}
          </React.Fragment>
        );
      })}
    </Stack>
  );
};

export { TermsFullContent };
